import React, { createContext, useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { inviteContact as inviteContactApi } from "../../api/relationships.api";
import { useToastContext } from "../../containers/ToastContextWrapper";
import { getErrorMessage } from "../../utils/errors";

type InviteNavigationConfig = {
  redirectTo?: string | null;
  redirectState?: Record<string, unknown>;
} | null;

type InviteContactArgs = {
  relationshipId: string;
  relatedAccountsAccessIds?: string[];
  redirectTo?: string | null;
  redirectState?: Record<string, unknown>;
};

interface ContactInviteContextType {
  inviteContact: (args: InviteContactArgs) => Promise<void>;
  isInvitingContact: boolean;
}

const ContactInviteContext = createContext<ContactInviteContextType | null>(
  null
);

export const ContactInviteContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useToastContext();

  const pendingNavRef = useRef<InviteNavigationConfig>(null);

  const { mutateAsync: inviteContactMutation, isPending: isInvitingContact } =
    useMutation({
      mutationFn: inviteContactApi,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["contacts"] });

        showToast({
          title: "Contact invited",
          description: "Contact invited successfully",
        });

        const navConfig = pendingNavRef.current;
        pendingNavRef.current = null;
        const targetPath =
          typeof navConfig?.redirectTo === "undefined"
            ? "/contacts"
            : navConfig?.redirectTo;
        if (targetPath) {
          navigate(targetPath, {
            replace: false,
            state: navConfig?.redirectState,
          });
        }
      },
      onError: (error: Error) => {
        pendingNavRef.current = null;
        showToast({
          title: "Error",
          description: getErrorMessage(error),
          type: "error",
        });
      },
    });

  const inviteContact = useCallback(
    async ({
      relationshipId,
      relatedAccountsAccessIds,
      redirectTo,
      redirectState,
    }: InviteContactArgs) => {
      pendingNavRef.current = { redirectTo, redirectState };
      await inviteContactMutation({
        relationshipId,
        relatedAccountsAccessIds: relatedAccountsAccessIds ?? [],
      });
    },
    [inviteContactMutation]
  );

  return (
    <ContactInviteContext.Provider value={{ inviteContact, isInvitingContact }}>
      {children}
    </ContactInviteContext.Provider>
  );
};

export const useContactInviteContext = () => {
  const context = useContext(ContactInviteContext);
  if (!context) {
    throw new Error(
      "useContactInviteContext must be used within a ContactInviteContextWrapper"
    );
  }
  return context;
};
